import {
  AULA_NOTIFICATION_HOVER_BG_COLOUR,
  Avatar,
  Badge,
  Box,
  Chip,
  Text,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React from 'react';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useSelector, useDispatch } from 'react-redux';
import { selectors, operationsResponsive } from '../../../store/conversations';
import { currentUserId } from '../../../../core/selectors/user';
import { conversationLatestMessage } from '../../../../core/conversations/selectorsResponsive';
import { online } from '../../../../core/selectors/live';
import { DARK } from '@core/constants/theme';
import { BeenhereOutlined, PeopleAltOutlined } from '@mui/icons-material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

interface ConversationListCardProps {
  conversation: any;
  selected?: boolean;
  onClick?: () => void;
  conversationMuted?: boolean;
}

const ConversationListCard: React.FC<ConversationListCardProps> = ({
  conversation,
  selected,
  onClick,
  conversationMuted,
}) => {
  const { palette } = useTheme();

  const dispatch = useDispatch();

  // Handle click event
  const handleClick = () => {
    // If the conversation is closed (archived), unarchive it
    if (conversation?.closed) {
      dispatch(operationsResponsive.setConversationReopened(conversation.id));
    } else {
    }

    // Call the original onClick handler
    if (onClick) {
      onClick();
    }
  };

  const userId = useSelector(currentUserId);
  const isUnread = useSelector(
    (state: any) =>
      selectors.isConversationUnreadBy &&
      selectors.isConversationUnreadBy(state, conversation?.id, userId)
  );
  const latestMessage = useSelector((state: any) =>
    conversationLatestMessage(state, conversation?.id)
  );
  const participants = conversation?.participants;

  // Get the other participant for individual conversations to check online status
  const otherParticipantId = React.useMemo(() => {
    if (!participants || conversation?.isGroup) {
      return null;
    }
    const otherParticipants = Object.keys(participants).filter(
      (participantId) => participantId !== userId
    );
    return otherParticipants.length === 1 ? otherParticipants[0] : null;
  }, [participants, conversation?.isGroup, userId]);

  const isOtherParticipantOnline = useSelector((state: any) =>
    otherParticipantId ? online(state, otherParticipantId) : false
  );

  // Show badge when conversation has unread messages AND the latest message is NOT from current user
  const unreadCount = isUnread && latestMessage?.writer !== userId ? 1 : 0;

  // Get conversation title
  const getConversationTitle = () => {
    if (conversation?.title && conversation.title.length < 25) {
      return conversation?.title;
    }
    if (conversation?.title && conversation.title.length >= 25) {
      return `${conversation?.title.substring(0, 25)}...`;
    }

    // For direct messages, show the other participant's name
    if (participants && !conversation?.isGroup) {
      const otherParticipants = Object.values(participants).filter(
        (participant: any) => participant.id !== userId
      );
      if (otherParticipants.length > 0) {
        return (otherParticipants[0] as any).name || 'Unknown User';
      }
    }

    return conversation?.isGroup ? 'Group Conversation' : 'Direct Message';
  };

  // Get latest message preview
  const getMessagePreview = () => {
    if (!latestMessage) {
      return 'No messages yet';
    }

    // Check if preview exists first, then fallback to content
    if (latestMessage?.preview) {
      const previewText = latestMessage.preview;

      // Truncate long messages
      if (previewText && previewText.length > 40) {
        return `${previewText.substring(0, 30)}...`;
      }

      return previewText || 'New message';
    }

    if (latestMessage?.content) {
      let previewText = '';

      try {
        const parsedContent = JSON.parse(latestMessage.content);

        // Handle Draft.js format (blocks with text)
        if (parsedContent?.blocks?.length) {
          previewText = parsedContent.blocks
            .map((block) => block.text || '')
            .filter((text) => text.trim())
            .join(' ')
            .trim();
        }
        // Handle simple object with text property
        else if (parsedContent?.text) {
          previewText = parsedContent.text;
        }
        // Handle array of text items
        else if (Array.isArray(parsedContent)) {
          previewText = parsedContent
            .map((item) => (typeof item === 'string' ? item : item?.text || ''))
            .filter((text) => text.trim())
            .join(' ')
            .trim();
        }
        // Handle object with content property
        else if (parsedContent?.content) {
          previewText =
            typeof parsedContent.content === 'string'
              ? parsedContent.content
              : JSON.stringify(parsedContent.content);
        }
        // If no recognized structure but it's an object, try to extract any text values
        else if (typeof parsedContent === 'object') {
          const textValues: string[] = [];
          const extractText = (obj: any) => {
            if (typeof obj === 'string' && obj.trim()) {
              textValues.push(obj.trim());
            } else if (Array.isArray(obj)) {
              obj.forEach(extractText);
            } else if (obj && typeof obj === 'object') {
              Object.values(obj).forEach(extractText);
            }
          };
          extractText(parsedContent);
          previewText = textValues.join(' ').trim();
        }

        // If still no text found, use raw content as fallback
        if (!previewText) {
          previewText = latestMessage.content;
        }
      } catch (e) {
        // Not JSON, use raw content
        previewText = latestMessage.content;
      }

      // Truncate long messages
      if (previewText && previewText.length > 50) {
        return `${previewText.substring(0, 50)}...`;
      }

      return previewText || 'New message';
    }

    return 'New message';
  };

  // Format time ago
  const getTimeAgo = () => {
    if (!latestMessage?.createdAt) {
      return '';
    }

    const messageDate = new Date(latestMessage.createdAt);
    const now = new Date();
    const diff = now.getTime() - messageDate.getTime();

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30); // Approximate
    const years = Math.floor(days / 365); // Approximate

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${months}mo ago`;
    return `${years}y ago`;
  };

  return (
    <Box
      mt=".5rem"
      role="button"
      tabIndex={0}
      aria-label={`${unreadCount ? 'Unread Message' : 'Message'} from ${
        conversation?.title || 'Unknown'
      }`}
      width="100%"
      bgcolor={unreadCount > 0 ? palette.background.default : 'transparent'}
      sx={{
        padding: '0px',
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: (() => {
          if (selected && palette.mode === DARK)
            return 'rgba(208, 188, 250, 0.08)';
          if (selected && palette.mode !== DARK)
            return 'rgba(69, 33, 136, 0.08)';
          if (unreadCount > 0) return palette.background.default;
          return 'transparent';
        })(),
        '&:hover': {
          backgroundColor:
            palette.mode === DARK
              ? palette.common.black
              : AULA_NOTIFICATION_HOVER_BG_COLOUR,
        },
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      borderRadius="8px"
      onClick={handleClick}
    >
      <Box
        display="flex"
        gap="8px"
        padding="8px"
        borderRadius="4px"
        position="relative"
      >
        {/* Unread badge - top right corner */}
        {unreadCount > 0 && (
          <Badge
            color="primary"
            variant="dot"
            sx={{
              position: 'absolute',
              top: '18px',
              right: '12px',
            }}
          />
        )}
        <Box position="relative">
          {/* Show online status badge only for individual conversations when the other participant is online */}
          {!conversation?.isGroup && isOtherParticipantOnline ? (
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#44b700',
                  color: '#44b700',
                  '&::after': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    animation: 'ripple 1.2s infinite ease-in-out',
                    border: '1px solid currentColor',
                    content: '""',
                  },
                },
                '@keyframes ripple': {
                  '0%': {
                    transform: 'scale(.8)',
                    opacity: 1,
                  },
                  '100%': {
                    transform: 'scale(2.4)',
                    opacity: 0,
                  },
                },
              }}
            >
              <Avatar
                kind="circular"
                sizes="20px"
                src={conversation?.userImage || ''}
                fallbackText={
                  conversation?.educatorOnly ? (
                    <BeenhereOutlined />
                  ) : conversation?.isGroup ? (
                    <PeopleAltOutlined />
                  ) : (
                    conversation?.title?.substring(0, 1).toUpperCase()
                  )
                }
              />
            </Badge>
          ) : (
            <Avatar
              kind="circular"
              sizes="20px"
              src={conversation?.userImage || ''}
              fallbackText={
                conversation?.educatorOnly ? (
                  <BeenhereOutlined />
                ) : conversation?.isGroup ? (
                  <PeopleAltOutlined />
                ) : (
                  conversation?.title?.substring(0, 1).toUpperCase()
                )
              }
            />
          )}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          width="100%"
          gap="4px"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box flex="1">
              <Box display="flex" alignItems="center" gap="4px">
                <Text fontWeight="550" maxLines={1}>
                  {getConversationTitle()}
                </Text>
                <br />
                {conversationMuted && (
                  <VolumeOffIcon style={{ fontSize: '16px', color: '#666' }} />
                )}
              </Box>

              <Box display="flex" alignItems="center" gap="4px">
                <Text
                  color={palette.mode === DARK ? '#FFFFFFB2' : '#00000099'}
                  fontSize=".875rem"
                >
                  {getTimeAgo()}
                </Text>
              </Box>

              <Box>
                {conversation?.closed && (
                  <Chip
                    size="small"
                    label="Archived"
                    icon={<ArchiveOutlinedIcon style={{ fontSize: '14px' }} />}
                    sx={{
                      height: '20px',
                      fontSize: '11px',
                      backgroundColor:
                        palette.mode === DARK
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.08)',
                      color:
                        palette.mode === DARK
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.6)',
                    }}
                  />
                )}
              </Box>

              <Box
                bgcolor={palette.background.paper}
                borderRadius="8px"
                mt="4px"
                py="4px"
                px="4px"
                width={'250px'}
              >
                <Text
                  overflow={'hidden'}
                  fontSize="0.875rem"
                  fontWeight="normal"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '0.17px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getMessagePreview()}
                </Text>
              </Box>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              gap="4px"
            ></Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationListCard;
